import { createContext, useContext, useEffect, useState, useCallback } from "react";
import i18n, { SUPPORTED_CODES } from "../i18n";
import api from "../lib/api";
import { setToken, clearToken } from "../lib/authStore";

const AuthCtx = createContext(null);

// Align the i18n language with a user's saved preference (if valid).
function syncLanguageFromUser(u) {
  const lng = u?.preferred_language;
  if (lng && SUPPORTED_CODES.includes(lng) && i18n.resolvedLanguage !== lng) {
    i18n.changeLanguage(lng);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Session restore: the HttpOnly cookie is sent automatically by the browser.
  // The backend re-issues a fresh JWT in the body so we can hold it in memory
  // for the WebSocket handshake.
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data.token) setToken(data.token);
      setUser(data.user);
      syncLanguageFromUser(data.user);
    } catch {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    syncLanguageFromUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setToken(data.token);
    setUser(data.user);
    syncLanguageFromUser(data.user);
    return data.user;
  };

  const logout = async () => {
    // Ask the backend to clear both HttpOnly cookies. Even if the network
    // fails, still purge in-memory state so the UI returns to /auth.
    try { await api.post("/auth/logout"); } catch { /* offline logout still valid */ }
    clearToken();
    setUser(null);
  };

  // Change the interface language. Updates i18n immediately (optimistic) and
  // persists to the user's profile when signed in.
  const changeLanguage = useCallback(async (code) => {
    if (!SUPPORTED_CODES.includes(code)) return;
    i18n.changeLanguage(code);
    setUser((prev) => (prev ? { ...prev, preferred_language: code } : prev));
    try {
      await api.patch("/profile", { preferred_language: code });
    } catch { /* language still applied locally; retry on next save */ }
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, setUser, changeLanguage }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
